import { useEffect, useMemo, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, ArrowLeft, Save, Settings } from "lucide-react";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useTaxProfile, useUpdateTaxProfile } from "@/lib/api/taxes.queries";
import type {
  TaxAutorretencionConfig,
  TaxObligation,
  TaxPeriodicity,
} from "@/lib/api/taxes";
import { ApiError } from "@/lib/api";
import { useActiveCompany } from "@/lib/company-context";

export const Route = createFileRoute(
  "/companies/$companyId/impuestos/configuracion",
)({
  component: TaxConfigPage,
});

const PERIODICITY_LABEL: Record<TaxPeriodicity, string> = {
  monthly: "Mensual",
  bimonthly: "Bimestral",
  quarterly: "Trimestral",
  four_monthly: "Cuatrimestral",
  annual: "Anual",
  manual: "Manual",
};

type DraftObligation = Pick<
  TaxObligation,
  | "taxKind"
  | "displayName"
  | "authority"
  | "formCode"
  | "paymentRequired"
  | "basis"
  | "rutConflict"
> & {
  active: boolean;
  periodicity: TaxPeriodicity;
  rate: string;
  notes: string;
  /** The stored config, sent back unchanged (the autorretencion key is rebuilt from the inputs below). */
  config: Record<string, unknown>;
  autorretencionCiiu: string;
  autorretencionRate: string;
  autorretencionNote: string;
};

/** Reads `config.autorretencion` leniently: a stored value that isn't one reads as unset. */
function readAutorretencion(
  config: Record<string, unknown>,
): TaxAutorretencionConfig {
  const raw = config.autorretencion;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const record = raw as Record<string, unknown>;
  return {
    ciiu: typeof record.ciiu === "string" ? record.ciiu : undefined,
    ratePercent:
      typeof record.ratePercent === "number" ? record.ratePercent : undefined,
    note: typeof record.note === "string" ? record.note : undefined,
  };
}

/**
 * The config a row sends back: every row keeps its stored config; the
 * retencion_fuente row rebuilds `autorretencion` from its inputs, omitting the
 * key when the three are empty so the API clears a stored one.
 */
function configForSave(obligation: DraftObligation): Record<string, unknown> {
  if (obligation.taxKind !== "retencion_fuente") return obligation.config;
  const rest: Record<string, unknown> = { ...obligation.config };
  delete rest.autorretencion;
  const ciiu = obligation.autorretencionCiiu.trim();
  const rate = obligation.autorretencionRate.trim();
  const note = obligation.autorretencionNote.trim();
  if (!ciiu && !rate && !note) return rest;
  const autorretencion: TaxAutorretencionConfig = {};
  if (ciiu) autorretencion.ciiu = ciiu;
  if (rate) autorretencion.ratePercent = Number(rate);
  if (note) autorretencion.note = note;
  return { ...rest, autorretencion };
}

function TaxConfigPage() {
  return (
    <RequireAuth>
      <AppShell>
        <TaxConfigContent />
      </AppShell>
    </RequireAuth>
  );
}

function TaxConfigContent() {
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();
  const profileQuery = useTaxProfile(companyId);
  const updateProfile = useUpdateTaxProfile(companyId);
  const [draft, setDraft] = useState<DraftObligation[]>([]);

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (!profileQuery.data) return;
    setDraft(
      profileQuery.data.obligations.map((obligation) => {
        const config = obligation.config ?? {};
        const autorretencion = readAutorretencion(config);
        return {
          taxKind: obligation.taxKind,
          displayName: obligation.displayName,
          authority: obligation.authority,
          formCode: obligation.formCode,
          paymentRequired: obligation.paymentRequired,
          basis: obligation.basis ?? [],
          rutConflict: obligation.rutConflict ?? [],
          active: obligation.active,
          periodicity: obligation.periodicity,
          rate: obligation.rate === null ? "" : String(obligation.rate),
          notes: obligation.notes ?? "",
          config,
          autorretencionCiiu: autorretencion.ciiu ?? "",
          autorretencionRate:
            autorretencion.ratePercent === undefined
              ? ""
              : String(autorretencion.ratePercent),
          autorretencionNote: autorretencion.note ?? "",
        };
      }),
    );
  }, [profileQuery.data]);

  const composition = profileQuery.data?.composition;
  const activeCount = useMemo(
    () => draft.filter((obligation) => obligation.active).length,
    [draft],
  );

  function updateDraft(
    taxKind: string,
    patch: Partial<Omit<DraftObligation, "taxKind">>,
  ) {
    setDraft((items) =>
      items.map((item) =>
        item.taxKind === taxKind ? { ...item, ...patch } : item,
      ),
    );
  }

  function save(e: React.FormEvent) {
    e.preventDefault();
    const retefuente = draft.find(
      (obligation) => obligation.taxKind === "retencion_fuente",
    );
    if (retefuente) {
      const ciiu = retefuente.autorretencionCiiu.trim();
      const rate = retefuente.autorretencionRate.trim();
      if (ciiu && !/^\d{3,4}$/.test(ciiu)) {
        toast.error(
          "El CIIU de la actividad principal tiene cuatro digitos (casillas 46 a 50 del RUT)",
        );
        return;
      }
      if (
        rate &&
        (!Number.isFinite(Number(rate)) ||
          Number(rate) < 0 ||
          Number(rate) > 100)
      ) {
        toast.error(
          "La tarifa de autorretencion es un porcentaje entre 0 y 100",
        );
        return;
      }
      if (rate && !retefuente.autorretencionNote.trim()) {
        toast.error(
          "Una tarifa de autorretencion fijada por el contador requiere una nota que explique por que difiere de la tabla",
        );
        return;
      }
    }
    updateProfile.mutate(
      {
        obligations: draft.map((obligation) => ({
          taxKind: obligation.taxKind,
          active: obligation.active,
          periodicity: obligation.periodicity,
          rate: obligation.rate.trim() ? Number(obligation.rate) : null,
          notes: obligation.notes.trim() || null,
          config: configForSave(obligation),
        })),
      },
      {
        onSuccess: () => toast.success("Configuracion tributaria guardada"),
        onError: (err) =>
          toast.error(
            err instanceof ApiError &&
              err.code === "TAX_OBLIGATION_CONFIG_INVALID"
              ? "Configuracion de autorretencion invalida"
              : "No pudimos guardar la configuracion",
            { description: err.message },
          ),
      },
    );
  }

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6 p-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1.5">
          <Link
            to="/companies/$companyId/impuestos"
            params={{ companyId }}
            className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Volver a impuestos
          </Link>
          <div className="flex items-center gap-2">
            <Settings className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-semibold tracking-tight">
              Configuracion tributaria
            </h1>
          </div>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Ajusta obligaciones, periodicidad y tarifas que no se pueden inferir
            completamente desde el RUT y los documentos.
          </p>
        </div>
        <Button
          type="submit"
          form="tax-config-form"
          disabled={updateProfile.isPending || !profileQuery.data?.editable}
        >
          <Save className="h-4 w-4" />
          Guardar
        </Button>
      </header>

      {profileQuery.isPending ? (
        <Skeleton className="h-96 rounded-lg" />
      ) : profileQuery.isError ? (
        <ErrorState
          title="No pudimos cargar la configuracion"
          description={profileQuery.error?.message ?? ""}
          onRetry={() => void profileQuery.refetch()}
          retryLabel="Reintentar"
        />
      ) : profileQuery.data ? (
        <>
          {!profileQuery.data.editable ? (
            <Alert variant="warning">
              <AlertTitle>Solo lectura</AlertTitle>
              <AlertDescription>
                Tu rol puede ver la configuracion, pero no editar obligaciones
                ni valores.
              </AlertDescription>
            </Alert>
          ) : null}

          <section className="grid gap-3 md:grid-cols-4">
            <Metric title="Obligaciones activas" value={String(activeCount)} />
            <Metric
              title="Regimen"
              value={composition?.regimeCode ?? "Sin dato"}
            />
            <Metric
              title="Responsabilidades"
              value={String(composition?.responsibilities.length ?? 0)}
            />
            <Metric
              title="Ciudad"
              value={composition?.cityCode ?? "Sin ciudad"}
            />
          </section>

          <form id="tax-config-form" className="grid gap-4" onSubmit={save}>
            {draft.map((obligation) => (
              <Card key={obligation.taxKind}>
                <CardHeader>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <CardTitle className="text-base">
                        {obligation.displayName}
                      </CardTitle>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <Badge variant="outline">
                          {obligation.authority.toUpperCase()}
                        </Badge>
                        {obligation.formCode ? (
                          <Badge variant="secondary">
                            Formulario {obligation.formCode}
                          </Badge>
                        ) : null}
                        {!obligation.paymentRequired ? (
                          <Badge variant="muted">Informativo</Badge>
                        ) : null}
                        {obligation.rutConflict.length ? (
                          <Badge variant="warning">
                            <AlertTriangle className="mr-1 h-3 w-3" />
                            RUT pide {obligation.rutConflict.join(", ")}
                          </Badge>
                        ) : null}
                      </div>
                      {obligation.basis.length ? (
                        <ul className="mt-2 space-y-0.5 text-xs text-muted-foreground">
                          {obligation.basis.map((line) => (
                            <li key={line}>{line}</li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={obligation.active}
                        disabled={!profileQuery.data.editable}
                        onChange={(e) =>
                          updateDraft(obligation.taxKind, {
                            active: e.target.checked,
                          })
                        }
                        className="h-4 w-4"
                      />
                      Activa
                    </label>
                  </div>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-[220px_180px_1fr]">
                  <div className="grid gap-1.5">
                    <Label>Periodicidad</Label>
                    <Select
                      value={obligation.periodicity}
                      disabled={!profileQuery.data.editable}
                      onValueChange={(value) =>
                        updateDraft(obligation.taxKind, {
                          periodicity: value as TaxPeriodicity,
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(PERIODICITY_LABEL).map(
                          ([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor={`${obligation.taxKind}-rate`}>
                      Tarifa %
                    </Label>
                    <Input
                      id={`${obligation.taxKind}-rate`}
                      inputMode="decimal"
                      value={obligation.rate}
                      disabled={!profileQuery.data.editable}
                      onChange={(e) =>
                        updateDraft(obligation.taxKind, {
                          rate: e.target.value,
                        })
                      }
                      placeholder="0"
                    />
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor={`${obligation.taxKind}-notes`}>Notas</Label>
                    <Input
                      id={`${obligation.taxKind}-notes`}
                      value={obligation.notes}
                      disabled={!profileQuery.data.editable}
                      onChange={(e) =>
                        updateDraft(obligation.taxKind, {
                          notes: e.target.value,
                        })
                      }
                      placeholder="Criterio, ciudad, actividad o referencia"
                    />
                  </div>
                  {obligation.taxKind === "retencion_fuente" ? (
                    <div className="grid gap-4 border-t pt-4 md:col-span-3 md:grid-cols-[220px_180px_1fr]">
                      <p className="text-xs text-muted-foreground md:col-span-3">
                        Autorretencion especial de renta: la tarifa sale de la
                        tabla del DUR 1625 para el CIIU de la actividad
                        principal; una tarifa fijada por el contador la
                        reemplaza y requiere una nota.
                      </p>
                      <div className="grid gap-1.5">
                        <Label htmlFor={`${obligation.taxKind}-ciiu`}>
                          CIIU actividad principal
                        </Label>
                        <Input
                          id={`${obligation.taxKind}-ciiu`}
                          inputMode="numeric"
                          maxLength={4}
                          value={obligation.autorretencionCiiu}
                          disabled={!profileQuery.data.editable}
                          onChange={(e) =>
                            updateDraft(obligation.taxKind, {
                              autorretencionCiiu: e.target.value,
                            })
                          }
                          placeholder="4 digitos, ej. 6201"
                        />
                      </div>
                      <div className="grid gap-1.5">
                        <Label
                          htmlFor={`${obligation.taxKind}-autorretencion-rate`}
                        >
                          Tarifa autorretencion % (fija el contador)
                        </Label>
                        <Input
                          id={`${obligation.taxKind}-autorretencion-rate`}
                          inputMode="decimal"
                          value={obligation.autorretencionRate}
                          disabled={!profileQuery.data.editable}
                          onChange={(e) =>
                            updateDraft(obligation.taxKind, {
                              autorretencionRate: e.target.value,
                            })
                          }
                          placeholder="Tabla"
                        />
                      </div>
                      <div className="grid gap-1.5">
                        <Label
                          htmlFor={`${obligation.taxKind}-autorretencion-note`}
                        >
                          Nota
                          {obligation.autorretencionRate.trim()
                            ? " (requerida con tarifa)"
                            : ""}
                        </Label>
                        <Input
                          id={`${obligation.taxKind}-autorretencion-note`}
                          value={obligation.autorretencionNote}
                          disabled={!profileQuery.data.editable}
                          onChange={(e) =>
                            updateDraft(obligation.taxKind, {
                              autorretencionNote: e.target.value,
                            })
                          }
                          placeholder="Por que la tarifa difiere de la tabla"
                        />
                      </div>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            ))}
          </form>
        </>
      ) : null}
    </div>
  );
}

function Metric(props: { title: string; value: string }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <p className="text-xs font-medium text-muted-foreground">{props.title}</p>
      <p className="mt-2 text-xl font-semibold">{props.value}</p>
    </div>
  );
}
